import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Set user from localStorage initially
          setUser(JSON.parse(storedUser));

          // Then verify with the backend
          const { data } = await apiClient.auth.getProfile();
          setUser(data.user);

          // Update stored user data
          localStorage.setItem('user', JSON.stringify(data.user));
        } catch (error) {
          // Only log the user out if the token was actually rejected (401).
          // A transient network error or a backend 5xx must NOT wipe the
          // session — otherwise a momentary hiccup bounces them to login.
          console.error('Auth initialization error:', error);
          if (error?.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          }
          // else: keep the user we already restored from localStorage above.
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  // Register a new user
  const register = async (userData) => {
    try {
      const { data } = await apiClient.auth.register(userData);
      setUser(data.user);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      return { success: true, data };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  // Login user
  const login = async (credentials) => {
    try {
      const { data } = await apiClient.auth.login(credentials);
      setUser(data.user);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      return { success: true, data };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  // Logout user
  const logout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      const { data } = await apiClient.auth.updateProfile(userData);
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      return { success: true, data };
    } catch (error) {
      console.error('Profile update error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Profile update failed'
      };
    }
  };

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Provide auth context value
  const value = {
    user,
    loading,
    isAuthenticated,
    register,
    login,
    logout,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;